import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarSettings } from './CalendarSettings';
import { Channels } from './Channels';
import { DayWindowSetting } from './DayWindowSetting';
import { ProfileSetting } from './ProfileSetting';
import { Sessions } from './Sessions';
import { YourData } from './YourData';
import { QueryState } from './QueryState';
import { useAccountDay } from '../accountDay';
import { readableInstant } from '../dateLabels';
import { MultiSelect } from './MultiSelect';
import {
  listTokens,
  createApiToken,
  revokeApiToken,
  listProjects,
  SCOPE_LIST,
  NARROWABLE_SCOPES,
  ApiError,
  type TokenSummary,
  getHealth,
} from '../api';

const DEFAULT_SCOPES = ['todos:read', 'todos:write', 'goals:read', 'goals:write'];

function NewTokenForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(DEFAULT_SCOPES);
  const [ttlDays, setTtlDays] = useState('90');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [issued, setIssued] = useState<{ plaintext: string; summary: string[] } | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: () => listProjects() });

  /*
    The picker appears only when every chosen scope *can* be narrowed.

    A goal, a habit and a calendar have no project, so a token limited to one would read none of
    them — which is why the server refuses that combination outright. Hiding the control rather
    than showing a disabled one: the question "which projects" is meaningless for those scopes, and
    an always-visible field that sometimes cannot be used is a field people learn to distrust. Tick
    `habits:read` with a project chosen and the limit is dropped along with the control, so the
    form never sends a body the server is about to refuse.
  */
  const narrowable = scopes.length > 0 && scopes.every((s) => NARROWABLE_SCOPES.includes(s));
  const limited = narrowable && projectIds.length > 0;

  const create = useMutation({
    mutationFn: () =>
      createApiToken({
        name: name.trim(),
        scopes,
        projectIds: limited ? projectIds : null,
        ttlDays: ttlDays ? Number(ttlDays) : null,
      }),
    onSuccess: (result) => {
      setIssued({ plaintext: result.plaintext, summary: result.summary });
      setName('');
      onCreated();
    },
  });

  const toggle = (scope: string) =>
    setScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );

  if (issued) {
    return (
      <div className="card">
        <h2>Copy this token now</h2>
        <p className="muted">
          It is shown once and never again. Only a hash is stored, so it cannot be recovered.
        </p>
        <pre className="token-plaintext">{issued.plaintext}</pre>
        <h3>What it can do</h3>
        <ul>
          {issued.summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <button onClick={() => setIssued(null)}>Done</button>
      </div>
    );
  }

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && scopes.length) create.mutate();
      }}
    >
      <h2>New token</h2>
      <p className="muted">
        For Claude or any MCP client. Grant the least it needs — you can always issue another.
      </p>

      <div className="fields">
        <label>
          Name
          <input
            value={name}
            placeholder="claude-desktop"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Expires in (days)
          <input
            type="number"
            min={1}
            value={ttlDays}
            placeholder="90"
            onChange={(e) => setTtlDays(e.target.value)}
          />
        </label>
      </div>

      <h3>Scopes</h3>
      <ul className="scope-picker">
        {SCOPE_LIST.map((scope) => (
          <li key={scope}>
            <label className={`check ${scope === 'delete' ? 'dangerous' : ''}`}>
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={() => toggle(scope)}
              />
              {scope}
              {scope === 'delete' && ' — permanent deletion, off by default'}
            </label>
          </li>
        ))}
      </ul>

      {narrowable && (
        <>
          <h3>Limit to projects</h3>
          <p className="muted">
            Leave this empty and the token reaches the whole account. Name projects and it reaches
            those and nothing else — not your personal tasks, notes or labels, and not your other
            projects. Anything outside them answers as though it does not exist.
          </p>
          <MultiSelect
            options={(projects.data ?? []).map((p) => ({ value: p.id, label: p.title }))}
            value={projectIds}
            onChange={setProjectIds}
            placeholder="The whole account"
            ariaLabel="Limit this token to projects"
            dialogTitle="Limit to projects"
          />
        </>
      )}

      {create.error instanceof ApiError && <p className="banner">{create.error.message}</p>}

      <div className="detail-foot">
        <button
          type="submit"
          className="primary"
          disabled={!name.trim() || scopes.length === 0 || create.isPending}
        >
          Create token
        </button>
      </div>
    </form>
  );
}

function TokenRow({ token }: { token: TokenSummary }) {
  const queryClient = useQueryClient();
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => listProjects() });
  const revoke = useMutation({
    mutationFn: () => revokeApiToken(token.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tokens'] }),
  });

  const expired = token.expiresAt !== null && new Date(token.expiresAt) < new Date();
  const dead = token.revokedAt !== null || expired;
  /* The account's clock, not this laptop's — see `dateLabels.ts`. */
  const { timezone } = useAccountDay();

  return (
    <li className={`token-row ${dead ? 'dead' : ''}`}>
      <div>
        <strong>{token.name}</strong> <code>{token.tokenPrefix}…</code>
      </div>
      <div className="scopes">
        {token.scopes.map((s) => (
          <span key={s} className={`pill ${s === 'delete' ? 'bad' : ''}`}>
            {s}
          </span>
        ))}
      </div>
      {/*
        What the token reaches, beside what it may do.

        Only when it is limited: `null` is every token minted before project limits existed and
        every one minted without the picker, and a "the whole account" line on all of them would be
        noise on the common case. A project whose title has not loaded — or which the account has
        since left — is shown by id rather than dropped, because a credential reaching something
        this screen cannot name is the one case somebody needs to see.
      */}
      {token.projectIds !== null && (
        <div className="scopes">
          {token.projectIds.map((id) => (
            <span key={id} className="pill">
              {projects.data?.find((p) => p.id === id)?.title ?? id}
            </span>
          ))}
        </div>
      )}
      <div className="muted token-meta">
        {token.revokedAt
          ? `revoked ${readableInstant(token.revokedAt, timezone, 'day')}`
          : expired
            ? 'expired'
            : token.expiresAt
              ? `expires ${readableInstant(token.expiresAt, timezone, 'day')}`
              : 'no expiry'}
        {' · '}
        {token.lastUsedAt
          ? `last used ${readableInstant(token.lastUsedAt, timezone)}`
          : 'never used'}
        {!dead && (
          <button className="link danger" onClick={() => revoke.mutate()}>
            revoke
          </button>
        )}
      </div>
    </li>
  );
}

/** The rail's contents, in the order the page renders them. */
const SECTIONS = [
  { id: 'you', label: 'You' },
  { id: 'day', label: 'Day view' },
  { id: 'tokens', label: 'Tokens' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'data', label: 'Your data' },
  { id: 'about', label: 'About' },
] as const;

export function Settings() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const tokens = useQuery({ queryKey: ['tokens'], queryFn: listTokens });
  // Shares the cache with the header's own health poll, so this costs nothing.
  const version = useQuery({ queryKey: ['health'], queryFn: getHealth });

  /*
    Which section the reader is in, so the rail says where they are rather than only where they
    could go. Topmost intersecting section wins: several are on screen at once on a tall display,
    and the one whose heading you have most recently passed is the one you are reading.
  */
  const [here, setHere] = useState<string>(SECTIONS[0].id);
  useEffect(() => {
    const seen = new Map<string, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) seen.set(e.target.id, e.isIntersecting);
        const first = SECTIONS.find((s) => seen.get(s.id));
        if (first) setHere(first.id);
      },
      { rootMargin: '-80px 0px -60% 0px' },
    );
    for (const s of SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div className="settings-layout">
      {/*
        A rail, because eight sections and 3,300 pixels had no navigation of any kind.

        Jump links rather than swapped panels: the page stays one page, so the browser's own find
        still crosses the whole of it, and a link cannot strand you in a section with no way back.
        The rail marks where you are as you scroll, which is the part that makes a long page
        navigable rather than merely long.
      */}
      <nav className="settings-rail" aria-label="Settings sections">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className={here === s.id ? 'is-here' : ''}
            aria-current={here === s.id ? 'true' : undefined}
          >
            {s.label}
          </a>
        ))}
      </nav>

      <section className="settings-body">
        {/* First on the screen: the timezone changes how every date in the app is read, which
            makes it the setting most worth getting right and the one hardest to notice is
            wrong. */}
        <section id="you" className="settings-section">
          <ProfileSetting />
        </section>
        <section id="day" className="settings-section">
          <DayWindowSetting />
        </section>

        {/*
        The heading first, then the control it belongs to.

        "New token" and its form rendered above `<h2>API tokens</h2>`, so the first thing on that
        part of the screen was a button with nothing yet said about what a token is or does — and
        the heading, when it arrived, appeared to introduce the list rather than the section.
      */}
        <section id="tokens" className="settings-section">
          <h2>API tokens</h2>
          <p className="muted">
            Every change made with one of these appears in the task’s activity thread, badged as an
            assistant.
          </p>

          <div className="capture">
            {/*
            The disclosure disappears while the form is open rather than relabelling itself `Cancel` —
            see `Habits.tsx` for the argument. One form, one way out, and it sits beside the commit.
          */}
            {!adding && <button onClick={() => setAdding(true)}>New token</button>}
          </div>

          {adding && (
            <NewTokenForm
              onCreated={() => queryClient.invalidateQueries({ queryKey: ['tokens'] })}
            />
          )}

          <QueryState query={tokens} />
          {tokens.data?.length === 0 && <p className="muted">No tokens yet.</p>}

          <ul className="token-list">
            {tokens.data?.map((token) => (
              <TokenRow key={token.id} token={token} />
            ))}
          </ul>
        </section>

        <section id="sessions" className="settings-section">
          <Sessions />
        </section>

        <section id="notifications" className="settings-section">
          <Channels />
        </section>
        <section id="calendar" className="settings-section">
          <CalendarSettings />
        </section>
        <section id="data" className="settings-section">
          <YourData />
        </section>

        {/*
        The version, which used to be printed on every screen in the header as
        `api 0.1.0 · db up`. It is a thing you look up when something is wrong, not a thing you
        need in your eyeline all day.
      */}
        <section id="about" className="settings-section">
          <h2>About</h2>
          <p className="muted">
            {version.isPending
              ? 'Checking…'
              : version.data
                ? `API ${version.data.version} · database ${version.data.database}`
                : 'The API could not be reached.'}
          </p>
        </section>
      </section>
    </div>
  );
}
